import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { paymentsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ChevronDown, ChevronUp, Loader2, Receipt } from 'lucide-react';

const STATUS_STYLE = {
  succeeded: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
  refunded: 'bg-gray-100 text-gray-700',
};

function when(value, withTime = false) {
  if (!value) return '';
  return new Date(value).toLocaleString(undefined, withTime
    ? { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { day: 'numeric', month: 'short', year: 'numeric' });
}

function money(p) {
  if (p.currency === 'NGN') return `₦${Number(p.amount).toLocaleString()}`;
  return `${p.currency === 'USD' ? '$' : `${p.currency} `}${Number(p.amount).toFixed(2)}`;
}

/** Profile -> Payments & receipts: every payment, each with its receipt. */
export default function PaymentsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [payments, setPayments] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    paymentsAPI.list()
      .then((res) => setPayments(res.data.payments || []))
      .catch(() => setPayments([]));
  }, []);

  return (
    <div className="p-4 max-w-2xl mx-auto animate-fadeIn" data-testid="payments-page">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold">{t('payments.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('payments.subtitle')}</p>
        </div>
      </div>

      {payments === null && <Loader2 className="w-8 h-8 animate-spin text-farm-primary mx-auto" />}
      {payments?.length === 0 && (
        <p className="text-muted-foreground text-center py-12" data-testid="payments-empty">{t('payments.empty')}</p>
      )}

      <div className="space-y-3">
        {(payments || []).map((p) => (
          <Card key={p.reference} data-testid={`payment-${p.reference}`}>
            <CardContent className="p-0">
              <button type="button" className="w-full p-4 flex items-center gap-3 text-left"
                      onClick={() => setOpen(open === p.reference ? null : p.reference)}>
                <Receipt className="w-5 h-5 text-farm-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{p.description}</p>
                  <p className="text-xs text-muted-foreground">{when(p.created_at)}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-semibold tabular-nums">{money(p)}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLE[p.status] || ''}`}>
                    {t(`payments.status.${p.status}`)}
                  </span>
                </div>
                {open === p.reference ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {open === p.reference && (
                <dl className="border-t px-4 py-3 text-sm grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5" data-testid="payment-receipt">
                  <dt className="text-muted-foreground">{t('payments.receipt.plan')}</dt><dd>{p.description}</dd>
                  <dt className="text-muted-foreground">{t('payments.receipt.amount')}</dt><dd>{money(p)}</dd>
                  {p.billing && (
                    <><dt className="text-muted-foreground">{t('payments.receipt.period')}</dt>
                      <dd data-testid="payment-period">{t(`payments.period.${p.billing === 'year' && p.months_charged === 10 ? 'yearOffer' : p.billing}`)}</dd></>
                  )}
                  <dt className="text-muted-foreground">{t('payments.receipt.date')}</dt><dd>{when(p.paid_at || p.created_at, true)}</dd>
                  {p.period_end && (
                    <><dt className="text-muted-foreground">{t('payments.receipt.paidUpTo')}</dt><dd>{when(p.period_end)}</dd></>
                  )}
                  <dt className="text-muted-foreground">{t('payments.receipt.method')}</dt>
                  <dd className="capitalize">{p.payment_method}</dd>
                  <dt className="text-muted-foreground">{t('payments.receipt.reference')}</dt>
                  <dd className="font-mono text-xs break-all">{p.reference}</dd>
                </dl>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
